const VISIBLE_WIDTH = 320;
const VISIBLE_HEIGHT = 200;
const SCREEN_SIZE = 1000;
const COLOR_SIZE = 500;
const BITMAP_SIZE = 8000;
const CELL_COLUMNS = 40;
const CELL_ROWS = 25;
const CELL_HEIGHT = 8;
const MODE_A_CELL_WIDTH = 8;
const MODE_BC_CELL_WIDTH = 4;
const PALETTE_SIZE = 16;
const MANUAL_ZOOM_MIN = 1;
const MANUAL_ZOOM_MAX = 8;
const ALL_PALETTE_INDICES = Array.from({ length: PALETTE_SIZE }, (_, value) => value);

const ORDERED_MATRIX_4X4 = [
  [0, 8, 2, 10],
  [12, 4, 14, 6],
  [3, 11, 1, 9],
  [15, 7, 13, 5]
] as const;

export type ConversionMode = "a" | "b" | "c";
export type ConversionFitMode = "contain" | "cover" | "stretch" | "manual";
export type ConversionDitherMode = "none" | "ordered" | "floyd";
export type ConversionPaletteStrategy = "keep-current" | "optimize-target" | "optimize-0-3";
export type ConversionPreset = "custom" | "photo" | "pixel-art" | "logo";

export type SourceImageState = {
  fileName: string;
  height: number;
  image: ImageBitmap;
  mimeType: string;
  width: number;
};

export type CropRect = {
  height: number;
  width: number;
  x: number;
  y: number;
};

export type ImageConversionSettings = {
  ditherMode: ConversionDitherMode;
  fitMode: ConversionFitMode;
  manualPanX: number;
  manualPanY: number;
  manualZoom: number;
  matteRgb444: number;
  mode: ConversionMode;
  paletteStrategy: ConversionPaletteStrategy;
  preset: ConversionPreset;
};

export type ConvertedDraftData = {
  bgColor: number;
  bitmap: Uint8Array;
  borderColor: number;
  color: Uint8Array;
  cropRect: CropRect;
  mode: ConversionMode;
  palette: Uint16Array;
  screen: Uint8Array;
  visibleRgba: Uint8ClampedArray;
};

export type SourcePreviewGeometry = {
  cropRect: CropRect;
  drawHeight: number;
  drawWidth: number;
  drawX: number;
  drawY: number;
  overlayHeight: number;
  overlayWidth: number;
  overlayX: number;
  overlayY: number;
};

type Rgb = {
  b: number;
  g: number;
  r: number;
};

type PairChoice = {
  first: number;
  second: number;
};

type ConversionWorkspace = {
  bitmap: Uint8Array;
  color: Uint8Array;
  context: CanvasRenderingContext2D | null;
  logicalRgba: Uint8ClampedArray;
  logicalDitherScratch: Float32Array;
  logicalDitheredRgba: Uint8ClampedArray;
  logicalVersion: number;
  palette: Uint16Array;
  screen: Uint8Array;
  visibleCanvas: HTMLCanvasElement | null;
  visibleCropRect: CropRect;
  visibleDitherScratch: Float32Array;
  visibleDitheredRgba: Uint8ClampedArray;
  visibleRgba: Uint8ClampedArray;
  visibleSource: SourceImageState | null;
  visibleStageKey: string | null;
  visibleVersion: number;
};

const workspace: ConversionWorkspace = {
  bitmap: new Uint8Array(BITMAP_SIZE),
  color: new Uint8Array(COLOR_SIZE),
  context: null,
  logicalRgba: new Uint8ClampedArray(160 * VISIBLE_HEIGHT * 4),
  logicalDitherScratch: new Float32Array(160 * VISIBLE_HEIGHT * 4),
  logicalDitheredRgba: new Uint8ClampedArray(160 * VISIBLE_HEIGHT * 4),
  logicalVersion: -1,
  palette: new Uint16Array(PALETTE_SIZE),
  screen: new Uint8Array(SCREEN_SIZE),
  visibleCanvas: null,
  visibleCropRect: { height: VISIBLE_HEIGHT, width: VISIBLE_WIDTH, x: 0, y: 0 },
  visibleDitherScratch: new Float32Array(VISIBLE_WIDTH * VISIBLE_HEIGHT * 4),
  visibleDitheredRgba: new Uint8ClampedArray(VISIBLE_WIDTH * VISIBLE_HEIGHT * 4),
  visibleRgba: new Uint8ClampedArray(VISIBLE_WIDTH * VISIBLE_HEIGHT * 4),
  visibleSource: null,
  visibleStageKey: null,
  visibleVersion: 0
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function clampByte(value: number): number {
  return clamp(Math.round(value), 0, 255);
}

function copyCropRect(rect: CropRect): CropRect {
  return {
    height: rect.height,
    width: rect.width,
    x: rect.x,
    y: rect.y
  };
}

function offscreenContext(): CanvasRenderingContext2D {
  if (workspace.context !== null) {
    return workspace.context;
  }
  const canvas = document.createElement("canvas");
  canvas.width = VISIBLE_WIDTH;
  canvas.height = VISIBLE_HEIGHT;
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("failed to create image conversion canvas context");
  }
  workspace.visibleCanvas = canvas;
  workspace.context = context;
  return context;
}

function resetDraftBuffers(): { bitmap: Uint8Array; color: Uint8Array; screen: Uint8Array } {
  workspace.bitmap.fill(0);
  workspace.screen.fill(0);
  workspace.color.fill(0);
  return {
    bitmap: workspace.bitmap,
    color: workspace.color,
    screen: workspace.screen
  };
}

function resetPaletteBuffer(basePalette: Uint16Array): Uint16Array {
  workspace.palette.fill(0);
  workspace.palette.set(basePalette.subarray(0, PALETTE_SIZE));
  return workspace.palette;
}

function visibleStageKey(source: SourceImageState, settings: ImageConversionSettings): string {
  return [
    source.width,
    source.height,
    settings.fitMode,
    settings.manualPanX.toFixed(4),
    settings.manualPanY.toFixed(4),
    settings.manualZoom.toFixed(4),
    settings.matteRgb444.toString(16)
  ].join("|");
}

function rgb444ToRgb(value: number): Rgb {
  return {
    b: (value & 0x0f) * 17,
    g: ((value >> 4) & 0x0f) * 17,
    r: ((value >> 8) & 0x0f) * 17
  };
}

function rgbToRgb444(r: number, g: number, b: number): number {
  return ((Math.round(r / 17) & 0x0f) << 8) | ((Math.round(g / 17) & 0x0f) << 4) | (Math.round(b / 17) & 0x0f);
}

function rgbDistance(left: Rgb, right: Rgb): number {
  const dr = left.r - right.r;
  const dg = left.g - right.g;
  const db = left.b - right.b;
  return dr * dr + dg * dg + db * db;
}

function paletteRgbList(palette: Uint16Array): Rgb[] {
  return Array.from(palette, (value) => rgb444ToRgb(value));
}

function histogramFromRgba(rgba: Uint8ClampedArray): Array<[number, number]> {
  const counts = new Map<number, number>();
  for (let offset = 0; offset < rgba.length; offset += 4) {
    const key = rgbToRgb444(rgba[offset], rgba[offset + 1], rgba[offset + 2]);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  return Array.from(counts.entries()).sort((left, right) => right[1] - left[1]);
}

function optimizedPaletteFromRgba(rgba: Uint8ClampedArray, count: number, fallback: Uint16Array): Uint16Array {
  const chosen = histogramFromRgba(rgba).slice(0, count).map(([value]) => value & 0x0fff);
  const palette = new Uint16Array(count);
  for (let index = 0; index < count; index += 1) {
    palette[index] = chosen[index] ?? fallback[index % fallback.length] ?? 0;
  }
  return palette;
}

function clonePalette(palette: Uint16Array): Uint16Array {
  const copy = new Uint16Array(palette.length);
  copy.set(palette);
  return copy;
}

function encode2bpp(c0: number, c1: number, c2: number, c3: number): number {
  return ((c0 & 0x03) << 6) | ((c1 & 0x03) << 4) | ((c2 & 0x03) << 2) | (c3 & 0x03);
}

function setPackedColorNibble(color: Uint8Array, cellIndex: number, nibble: number): void {
  const byteIndex = cellIndex >> 1;
  const current = color[byteIndex] ?? 0;
  if ((cellIndex & 1) === 0) {
    color[byteIndex] = (current & 0xf0) | (nibble & 0x0f);
    return;
  }
  color[byteIndex] = (current & 0x0f) | ((nibble & 0x0f) << 4);
}

function aspectCropRect(width: number, height: number): CropRect {
  const frameAspect = VISIBLE_WIDTH / VISIBLE_HEIGHT;
  const sourceAspect = width / height;
  if (sourceAspect >= frameAspect) {
    const cropWidth = height * frameAspect;
    return {
      height,
      width: cropWidth,
      x: (width - cropWidth) / 2,
      y: 0
    };
  }
  const cropHeight = width / frameAspect;
  return {
    height: cropHeight,
    width,
    x: 0,
    y: (height - cropHeight) / 2
  };
}

function clampSettings(settings: ImageConversionSettings): ImageConversionSettings {
  return {
    ...settings,
    manualPanX: clamp(settings.manualPanX, -1, 1),
    manualPanY: clamp(settings.manualPanY, -1, 1),
    manualZoom: clamp(settings.manualZoom, MANUAL_ZOOM_MIN, MANUAL_ZOOM_MAX),
    matteRgb444: settings.matteRgb444 & 0x0fff,
    paletteStrategy:
      settings.mode === "c"
        ? settings.paletteStrategy
        : settings.paletteStrategy === "optimize-0-3"
          ? "keep-current"
          : settings.paletteStrategy,
    preset: settings.preset
  };
}

function cropRectForSettings(source: SourceImageState, settings: ImageConversionSettings): CropRect {
  if (settings.fitMode === "contain" || settings.fitMode === "stretch") {
    return {
      height: source.height,
      width: source.width,
      x: 0,
      y: 0
    };
  }

  const coverRect = aspectCropRect(source.width, source.height);
  if (settings.fitMode === "cover") {
    return coverRect;
  }

  const zoom = clamp(settings.manualZoom, MANUAL_ZOOM_MIN, MANUAL_ZOOM_MAX);
  const width = coverRect.width / zoom;
  const height = coverRect.height / zoom;
  const availableX = coverRect.width - width;
  const availableY = coverRect.height - height;
  return {
    height,
    width,
    x: coverRect.x + ((settings.manualPanX + 1) * 0.5) * availableX,
    y: coverRect.y + ((settings.manualPanY + 1) * 0.5) * availableY
  };
}

function matteCss(rgb444: number): string {
  const rgb = rgb444ToRgb(rgb444);
  return `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;
}

function visibleImageData(source: SourceImageState, settings: ImageConversionSettings): { cropRect: CropRect; rgba: Uint8ClampedArray } {
  const safeSettings = clampSettings(settings);
  const stageKey = visibleStageKey(source, safeSettings);
  if (workspace.visibleSource === source && workspace.visibleStageKey === stageKey) {
    return {
      cropRect: copyCropRect(workspace.visibleCropRect),
      rgba: workspace.visibleRgba
    };
  }

  const context = offscreenContext();
  context.imageSmoothingEnabled = true;
  context.clearRect(0, 0, VISIBLE_WIDTH, VISIBLE_HEIGHT);
  context.fillStyle = matteCss(safeSettings.matteRgb444);
  context.fillRect(0, 0, VISIBLE_WIDTH, VISIBLE_HEIGHT);

  const cropRect = cropRectForSettings(source, safeSettings);
  if (safeSettings.fitMode === "stretch") {
    context.drawImage(source.image, 0, 0, VISIBLE_WIDTH, VISIBLE_HEIGHT);
  } else if (safeSettings.fitMode === "contain") {
    const scale = Math.min(VISIBLE_WIDTH / source.width, VISIBLE_HEIGHT / source.height);
    const drawWidth = source.width * scale;
    const drawHeight = source.height * scale;
    const drawX = (VISIBLE_WIDTH - drawWidth) / 2;
    const drawY = (VISIBLE_HEIGHT - drawHeight) / 2;
    context.drawImage(source.image, drawX, drawY, drawWidth, drawHeight);
  } else {
    context.drawImage(
      source.image,
      cropRect.x,
      cropRect.y,
      cropRect.width,
      cropRect.height,
      0,
      0,
      VISIBLE_WIDTH,
      VISIBLE_HEIGHT
    );
  }

  const imageData = context.getImageData(0, 0, VISIBLE_WIDTH, VISIBLE_HEIGHT);
  workspace.visibleRgba.set(imageData.data);
  workspace.visibleCropRect = copyCropRect(cropRect);
  workspace.visibleSource = source;
  workspace.visibleStageKey = stageKey;
  workspace.visibleVersion += 1;
  workspace.logicalVersion = -1;
  return {
    cropRect: copyCropRect(cropRect),
    rgba: workspace.visibleRgba
  };
}

export function sourcePreviewGeometry(
  source: SourceImageState,
  settings: ImageConversionSettings,
  canvasWidth: number,
  canvasHeight: number
): SourcePreviewGeometry {
  const scale = Math.min(canvasWidth / source.width, canvasHeight / source.height);
  const drawWidth = source.width * scale;
  const drawHeight = source.height * scale;
  const drawX = (canvasWidth - drawWidth) / 2;
  const drawY = (canvasHeight - drawHeight) / 2;
  const cropRect = cropRectForSettings(source, clampSettings(settings));
  return {
    cropRect,
    drawHeight,
    drawWidth,
    drawX,
    drawY,
    overlayHeight: cropRect.height * scale,
    overlayWidth: cropRect.width * scale,
    overlayX: drawX + cropRect.x * scale,
    overlayY: drawY + cropRect.y * scale
  };
}

export function renderSourcePreview(
  context: CanvasRenderingContext2D,
  source: SourceImageState,
  settings: ImageConversionSettings
): CropRect {
  const geometry = sourcePreviewGeometry(source, settings, context.canvas.width, context.canvas.height);
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
  context.fillStyle = "rgba(3, 10, 15, 0.96)";
  context.fillRect(0, 0, context.canvas.width, context.canvas.height);
  context.imageSmoothingEnabled = true;
  context.drawImage(source.image, geometry.drawX, geometry.drawY, geometry.drawWidth, geometry.drawHeight);

  context.save();
  context.fillStyle = "rgba(4, 10, 14, 0.58)";
  context.fillRect(0, 0, context.canvas.width, geometry.overlayY);
  context.fillRect(0, geometry.overlayY + geometry.overlayHeight, context.canvas.width, context.canvas.height - geometry.overlayY - geometry.overlayHeight);
  context.fillRect(0, geometry.overlayY, geometry.overlayX, geometry.overlayHeight);
  context.fillRect(
    geometry.overlayX + geometry.overlayWidth,
    geometry.overlayY,
    context.canvas.width - geometry.overlayX - geometry.overlayWidth,
    geometry.overlayHeight
  );
  context.strokeStyle = "rgba(242, 184, 75, 0.92)";
  context.lineWidth = 2;
  context.strokeRect(geometry.overlayX + 1, geometry.overlayY + 1, geometry.overlayWidth - 2, geometry.overlayHeight - 2);
  context.restore();

  return geometry.cropRect;
}

function nearestPaletteIndex(rgb: Rgb, palette: Rgb[], indices: number[], x: number, y: number, ditherMode: ConversionDitherMode): number {
  let compare = rgb;
  if (ditherMode === "ordered") {
    const threshold = ORDERED_MATRIX_4X4[y & 3][x & 3] - 7.5;
    const offset = threshold * 8;
    compare = {
      b: clampByte(rgb.b + offset),
      g: clampByte(rgb.g + offset),
      r: clampByte(rgb.r + offset)
    };
  }

  let bestIndex = indices[0] ?? 0;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const paletteIndex of indices) {
    const distance = rgbDistance(compare, palette[paletteIndex] ?? palette[0]!);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIndex = paletteIndex;
    }
  }
  return bestIndex;
}

function rgbaAt(rgba: Uint8ClampedArray, width: number, x: number, y: number): Rgb {
  const offset = (y * width + x) * 4;
  return {
    b: rgba[offset + 2] ?? 0,
    g: rgba[offset + 1] ?? 0,
    r: rgba[offset] ?? 0
  };
}

function floydDitherRgba(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  palette: Rgb[],
  indices: number[],
  scratch: Float32Array,
  output: Uint8ClampedArray
): Uint8ClampedArray {
  for (let offset = 0; offset < rgba.length; offset += 4) {
    scratch[offset] = rgba[offset] ?? 0;
    scratch[offset + 1] = rgba[offset + 1] ?? 0;
    scratch[offset + 2] = rgba[offset + 2] ?? 0;
    scratch[offset + 3] = 255;
  }

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const offset = (y * width + x) * 4;
      const current = {
        b: clampByte(scratch[offset + 2] ?? 0),
        g: clampByte(scratch[offset + 1] ?? 0),
        r: clampByte(scratch[offset] ?? 0)
      };
      const paletteIndex = nearestPaletteIndex(current, palette, indices, x, y, "none");
      const paletteRgb = palette[paletteIndex] ?? palette[0]!;
      output[offset] = paletteRgb.r;
      output[offset + 1] = paletteRgb.g;
      output[offset + 2] = paletteRgb.b;
      output[offset + 3] = 255;

      const errorR = current.r - paletteRgb.r;
      const errorG = current.g - paletteRgb.g;
      const errorB = current.b - paletteRgb.b;
      const diffuse = (targetX: number, targetY: number, factor: number): void => {
        if (targetX < 0 || targetX >= width || targetY < 0 || targetY >= height) {
          return;
        }
        const targetOffset = (targetY * width + targetX) * 4;
        scratch[targetOffset] += errorR * factor;
        scratch[targetOffset + 1] += errorG * factor;
        scratch[targetOffset + 2] += errorB * factor;
      };
      diffuse(x + 1, y, 7 / 16);
      diffuse(x - 1, y + 1, 3 / 16);
      diffuse(x, y + 1, 5 / 16);
      diffuse(x + 1, y + 1, 1 / 16);
    }
  }

  return output;
}

function logicalImageData(rgba: Uint8ClampedArray): Uint8ClampedArray {
  if (workspace.logicalVersion === workspace.visibleVersion) {
    return workspace.logicalRgba;
  }

  const logical = workspace.logicalRgba;
  for (let y = 0; y < VISIBLE_HEIGHT; y += 1) {
    for (let x = 0; x < 160; x += 1) {
      const leftOffset = (y * VISIBLE_WIDTH + x * 2) * 4;
      const rightOffset = leftOffset + 4;
      const offset = (y * 160 + x) * 4;
      logical[offset] = Math.round(((rgba[leftOffset] ?? 0) + (rgba[rightOffset] ?? 0)) * 0.5);
      logical[offset + 1] = Math.round(((rgba[leftOffset + 1] ?? 0) + (rgba[rightOffset + 1] ?? 0)) * 0.5);
      logical[offset + 2] = Math.round(((rgba[leftOffset + 2] ?? 0) + (rgba[rightOffset + 2] ?? 0)) * 0.5);
      logical[offset + 3] = 255;
    }
  }
  workspace.logicalVersion = workspace.visibleVersion;
  return logical;
}

function convertModeC(
  logicalRgba: Uint8ClampedArray,
  settings: ImageConversionSettings,
  basePalette: Uint16Array,
  baseBgColor: number,
  borderColor: number,
  cropRect: CropRect,
  visibleRgba: Uint8ClampedArray
): ConvertedDraftData {
  const palette = resetPaletteBuffer(basePalette);
  if (settings.paletteStrategy === "optimize-target" || settings.paletteStrategy === "optimize-0-3") {
    const optimized = optimizedPaletteFromRgba(logicalRgba, 4, basePalette);
    for (let index = 0; index < 4; index += 1) {
      palette[index] = optimized[index];
    }
  }
  const paletteRgb = paletteRgbList(palette);
  const { bitmap, color, screen } = resetDraftBuffers();
  const sourceRgba = settings.ditherMode === "floyd"
    ? floydDitherRgba(logicalRgba, 160, VISIBLE_HEIGHT, paletteRgb, [0, 1, 2, 3], workspace.logicalDitherScratch, workspace.logicalDitheredRgba)
    : logicalRgba;

  for (let cellY = 0; cellY < CELL_ROWS; cellY += 1) {
    for (let cellX = 0; cellX < CELL_COLUMNS; cellX += 1) {
      for (let row = 0; row < CELL_HEIGHT; row += 1) {
        const y = cellY * CELL_HEIGHT + row;
        const xBase = cellX * MODE_BC_CELL_WIDTH;
        const codes = [0, 0, 0, 0];
        for (let px = 0; px < MODE_BC_CELL_WIDTH; px += 1) {
          const rgb = rgbaAt(sourceRgba, 160, xBase + px, y);
          codes[px] = nearestPaletteIndex(rgb, paletteRgb, [0, 1, 2, 3], xBase + px, y, settings.ditherMode);
        }
        bitmap[cellY * 320 + cellX * 8 + row] = encode2bpp(codes[0], codes[1], codes[2], codes[3]);
      }
    }
  }

  return {
    bgColor: baseBgColor & 0x0f,
    bitmap,
    borderColor: borderColor & 0x0f,
    color,
    cropRect,
    mode: "c",
    palette,
    screen,
    visibleRgba
  };
}

function bestPairForCell(pixels: Rgb[], palette: Rgb[]): PairChoice {
  let best: PairChoice = { first: 0, second: 1 };
  let bestError = Number.POSITIVE_INFINITY;
  for (let first = 0; first < PALETTE_SIZE; first += 1) {
    for (let second = first + 1; second < PALETTE_SIZE; second += 1) {
      let error = 0;
      const firstRgb = palette[first]!;
      const secondRgb = palette[second]!;
      for (const pixel of pixels) {
        error += Math.min(rgbDistance(pixel, firstRgb), rgbDistance(pixel, secondRgb));
      }
      if (error < bestError) {
        bestError = error;
        best = { first, second };
      }
    }
  }
  return best;
}

function chooseModeABgFg(pixels: Rgb[], palette: Rgb[], pair: PairChoice): { bg: number; fg: number; bits: number[] } {
  const bits: number[] = [];
  let firstCount = 0;
  let secondCount = 0;
  for (const pixel of pixels) {
    const useFirst = rgbDistance(pixel, palette[pair.first]!) <= rgbDistance(pixel, palette[pair.second]!);
    bits.push(useFirst ? 0 : 1);
    if (useFirst) {
      firstCount += 1;
    } else {
      secondCount += 1;
    }
  }

  const bg = firstCount >= secondCount ? pair.first : pair.second;
  const fg = bg === pair.first ? pair.second : pair.first;
  return {
    bg,
    bits: bits.map((bit) => ((bit === 0 ? pair.first : pair.second) === fg ? 1 : 0)),
    fg
  };
}

function convertModeA(
  visibleRgba: Uint8ClampedArray,
  settings: ImageConversionSettings,
  basePalette: Uint16Array,
  baseBgColor: number,
  borderColor: number,
  cropRect: CropRect
): ConvertedDraftData {
  const palette = resetPaletteBuffer(basePalette);
  if (settings.paletteStrategy === "optimize-target") {
    const optimized = optimizedPaletteFromRgba(visibleRgba, PALETTE_SIZE, basePalette);
    palette.set(optimized);
  }
  const paletteRgb = paletteRgbList(palette);
  const { bitmap, color, screen } = resetDraftBuffers();
  const sourceRgba = settings.ditherMode === "floyd"
    ? floydDitherRgba(visibleRgba, VISIBLE_WIDTH, VISIBLE_HEIGHT, paletteRgb, ALL_PALETTE_INDICES, workspace.visibleDitherScratch, workspace.visibleDitheredRgba)
    : visibleRgba;

  for (let cellY = 0; cellY < CELL_ROWS; cellY += 1) {
    for (let cellX = 0; cellX < CELL_COLUMNS; cellX += 1) {
      const pixels: Rgb[] = [];
      for (let row = 0; row < CELL_HEIGHT; row += 1) {
        const y = cellY * CELL_HEIGHT + row;
        for (let col = 0; col < MODE_A_CELL_WIDTH; col += 1) {
          const x = cellX * MODE_A_CELL_WIDTH + col;
          let rgb = rgbaAt(sourceRgba, VISIBLE_WIDTH, x, y);
          if (settings.ditherMode === "ordered") {
            const threshold = ORDERED_MATRIX_4X4[y & 3][x & 3] - 7.5;
            const offset = threshold * 8;
            rgb = { b: clampByte(rgb.b + offset), g: clampByte(rgb.g + offset), r: clampByte(rgb.r + offset) };
          }
          pixels.push(rgb);
        }
      }

      const pair = bestPairForCell(pixels, paletteRgb);
      const { bg, fg, bits } = chooseModeABgFg(pixels, paletteRgb, pair);
      const cellIndex = cellY * CELL_COLUMNS + cellX;
      screen[cellIndex] = ((fg & 0x0f) << 4) | (bg & 0x0f);

      for (let row = 0; row < CELL_HEIGHT; row += 1) {
        let value = 0;
        for (let col = 0; col < MODE_A_CELL_WIDTH; col += 1) {
          value |= (bits[row * MODE_A_CELL_WIDTH + col] & 0x01) << (7 - col);
        }
        bitmap[cellY * 320 + cellX * 8 + row] = value;
      }
    }
  }

  return {
    bgColor: baseBgColor & 0x0f,
    bitmap,
    borderColor: borderColor & 0x0f,
    color,
    cropRect,
    mode: "a",
    palette,
    screen,
    visibleRgba
  };
}

function dominantPaletteIndex(rgba: Uint8ClampedArray, palette: Rgb[], ditherMode: ConversionDitherMode): number {
  const counts = new Array<number>(PALETTE_SIZE).fill(0);
  for (let y = 0; y < VISIBLE_HEIGHT; y += 1) {
    for (let x = 0; x < 160; x += 1) {
      const rgb = rgbaAt(rgba, 160, x, y);
      const index = nearestPaletteIndex(rgb, palette, ALL_PALETTE_INDICES, x, y, ditherMode);
      counts[index] += 1;
    }
  }
  let bestIndex = 0;
  let bestCount = -1;
  for (let index = 0; index < counts.length; index += 1) {
    if (counts[index] > bestCount) {
      bestCount = counts[index];
      bestIndex = index;
    }
  }
  return bestIndex;
}

function convertModeB(
  logicalRgba: Uint8ClampedArray,
  settings: ImageConversionSettings,
  basePalette: Uint16Array,
  baseBgColor: number,
  borderColor: number,
  cropRect: CropRect,
  visibleRgba: Uint8ClampedArray
): ConvertedDraftData {
  const palette = resetPaletteBuffer(basePalette);
  if (settings.paletteStrategy === "optimize-target") {
    const optimized = optimizedPaletteFromRgba(logicalRgba, PALETTE_SIZE, basePalette);
    palette.set(optimized);
  }
  const paletteRgb = paletteRgbList(palette);
  const sourceRgba = settings.ditherMode === "floyd"
    ? floydDitherRgba(logicalRgba, 160, VISIBLE_HEIGHT, paletteRgb, ALL_PALETTE_INDICES, workspace.logicalDitherScratch, workspace.logicalDitheredRgba)
    : logicalRgba;
  const bgColor = settings.paletteStrategy === "optimize-target"
    ? dominantPaletteIndex(sourceRgba, paletteRgb, settings.ditherMode === "floyd" ? "none" : settings.ditherMode)
    : (baseBgColor & 0x0f);
  const { bitmap, color, screen } = resetDraftBuffers();

  for (let cellY = 0; cellY < CELL_ROWS; cellY += 1) {
    for (let cellX = 0; cellX < CELL_COLUMNS; cellX += 1) {
      const cellCounts = new Array<number>(PALETTE_SIZE).fill(0);
      const cellPixels: Rgb[] = [];
      for (let row = 0; row < CELL_HEIGHT; row += 1) {
        const y = cellY * CELL_HEIGHT + row;
        for (let col = 0; col < MODE_BC_CELL_WIDTH; col += 1) {
          const x = cellX * MODE_BC_CELL_WIDTH + col;
          const rgb = rgbaAt(sourceRgba, 160, x, y);
          cellPixels.push(rgb);
          const nearest = nearestPaletteIndex(rgb, paletteRgb, ALL_PALETTE_INDICES, x, y, settings.ditherMode);
          cellCounts[nearest] += 1;
        }
      }

      const candidates = ALL_PALETTE_INDICES
        .filter((value) => value !== bgColor)
        .sort((left, right) => cellCounts[right] - cellCounts[left]);
      const hi = candidates[0] ?? bgColor;
      const lo = candidates[1] ?? bgColor;
      const ram = candidates[2] ?? bgColor;
      const cellIndex = cellY * CELL_COLUMNS + cellX;
      screen[cellIndex] = ((hi & 0x0f) << 4) | (lo & 0x0f);
      setPackedColorNibble(color, cellIndex, ram);
      const available = [bgColor, hi, lo, ram];

      for (let row = 0; row < CELL_HEIGHT; row += 1) {
        const y = cellY * CELL_HEIGHT + row;
        const xBase = cellX * MODE_BC_CELL_WIDTH;
        const codes = [0, 0, 0, 0];
        for (let col = 0; col < MODE_BC_CELL_WIDTH; col += 1) {
          const rgb = rgbaAt(sourceRgba, 160, xBase + col, y);
          let bestCode = 0;
          let bestDistance = Number.POSITIVE_INFINITY;
          for (let code = 0; code < available.length; code += 1) {
            const index = available[code] ?? bgColor;
            const distance = rgbDistance(rgb, paletteRgb[index]!);
            if (distance < bestDistance) {
              bestDistance = distance;
              bestCode = code;
            }
          }
          codes[col] = bestCode;
        }
        bitmap[cellY * 320 + cellX * 8 + row] = encode2bpp(codes[0], codes[1], codes[2], codes[3]);
      }
    }
  }

  return {
    bgColor,
    bitmap,
    borderColor: borderColor & 0x0f,
    color,
    cropRect,
    mode: "b",
    palette,
    screen,
    visibleRgba
  };
}

export function defaultConversionSettings(mode: ConversionMode, matteRgb444: number): ImageConversionSettings {
  return {
    ditherMode: "none",
    fitMode: "cover",
    manualPanX: 0,
    manualPanY: 0,
    manualZoom: 1,
    matteRgb444: matteRgb444 & 0x0fff,
    mode,
    paletteStrategy: mode === "c" ? "optimize-0-3" : "keep-current",
    preset: "custom"
  };
}

export async function decodeSourceImage(file: File): Promise<SourceImageState> {
  const mimeType = file.type.toLowerCase();
  if (mimeType !== "image/png" && mimeType !== "image/jpeg") {
    throw new Error("Image conversion currently supports PNG and JPEG files only.");
  }

  const image = await createImageBitmap(file);
  return {
    fileName: file.name,
    height: image.height,
    image,
    mimeType,
    width: image.width
  };
}

export function convertSourceImage(
  source: SourceImageState,
  settings: ImageConversionSettings,
  basePalette: Uint16Array,
  baseBgColor: number,
  borderColor: number
): ConvertedDraftData {
  // Performance note: this function reuses scratch buffers inside the module-level
  // workspace for preview updates. Callers that need a stable snapshot beyond the
  // current conversion pass must clone the returned arrays before storing them.
  const safeSettings = clampSettings(settings);
  const { cropRect, rgba: visibleRgba } = visibleImageData(source, safeSettings);
  if (safeSettings.mode === "a") {
    return convertModeA(visibleRgba, safeSettings, basePalette, baseBgColor, borderColor, cropRect);
  }
  const logicalRgba = logicalImageData(visibleRgba);
  if (safeSettings.mode === "c") {
    return convertModeC(logicalRgba, safeSettings, basePalette, baseBgColor, borderColor, cropRect, visibleRgba);
  }
  return convertModeB(logicalRgba, safeSettings, basePalette, baseBgColor, borderColor, cropRect, visibleRgba);
}
